/**
 * BashError - Base Error Class
 *
 * Standardized error class for the Bash system.
 * Includes error code, correlation ID, and structured context.
 */

import { ErrorCode, ErrorMessages, type ErrorCodeType } from './error-codes.js';

/**
 * Additional context for errors
 */
export interface BashErrorContext {
  sessionId?: string;
  agentId?: string;
  toolName?: string;
  toolUseId?: string;
  socketId?: string;
  [key: string]: unknown;
}

/**
 * Serialized error format for transmission
 */
export interface SerializedBashError {
  code: ErrorCodeType;
  message: string;
  correlationId?: string | undefined;
  context?: BashErrorContext | undefined;
  timestamp: string;
  stack?: string | undefined;
}

/**
 * BashError - Structured error class
 *
 * @example
 * throw new BashError(ErrorCode.SESSION_NOT_FOUND, {
 *   context: { sessionId: 'abc-123' },
 *   correlationId: 'req-456',
 * });
 */
export class BashError extends Error {
  public readonly code: ErrorCodeType;
  public readonly correlationId?: string | undefined;
  public readonly context?: BashErrorContext | undefined;
  public readonly timestamp: Date;
  public readonly cause?: Error | undefined;

  constructor(
    code: ErrorCodeType,
    options?: {
      message?: string | undefined;
      correlationId?: string | undefined;
      context?: BashErrorContext | undefined;
      cause?: Error | undefined;
    }
  ) {
    const message = options?.message ?? ErrorMessages[code] ?? 'Unknown error';
    super(message);

    this.name = 'BashError';
    this.code = code;
    this.correlationId = options?.correlationId;
    this.context = options?.context;
    this.timestamp = new Date();
    this.cause = options?.cause;

    // Maintains proper stack trace for where error was thrown
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, BashError);
    }
  }

  /**
   * Serialize error for transmission (e.g., over socket)
   */
  toJSON(): SerializedBashError {
    return {
      code: this.code,
      message: this.message,
      correlationId: this.correlationId,
      context: this.context,
      timestamp: this.timestamp.toISOString(),
      stack: process.env.NODE_ENV === 'development' ? this.stack : undefined,
    };
  }

  /**
   * Create error from serialized format
   */
  static fromJSON(data: SerializedBashError): BashError {
    return new BashError(data.code, {
      message: data.message,
      correlationId: data.correlationId,
      context: data.context,
    });
  }

  /**
   * Wrap an unknown error as BashError
   */
  static wrap(error: unknown, defaultCode: ErrorCodeType = ErrorCode.UNKNOWN_ERROR): BashError {
    if (error instanceof BashError) {
      return error;
    }

    if (error instanceof Error) {
      return new BashError(defaultCode, {
        message: error.message,
        cause: error,
      });
    }

    return new BashError(defaultCode, {
      message: String(error),
    });
  }

  /**
   * Check if an error is a BashError
   */
  static isBashError(error: unknown): error is BashError {
    return error instanceof BashError;
  }

  /**
   * Get user-friendly error message
   */
  get userMessage(): string {
    return ErrorMessages[this.code] ?? this.message;
  }

  /**
   * Create a detailed log string
   */
  toLogString(): string {
    const parts = [
      `[${this.code}]`,
      this.message,
      this.correlationId ? `(${this.correlationId})` : '',
      this.context ? JSON.stringify(this.context) : '',
    ];
    return parts.filter(Boolean).join(' ');
  }
}

// =============================================================================
// Factory Functions for Common Errors
// =============================================================================

export function sessionNotFoundError(sessionId: string, correlationId?: string): BashError {
  return new BashError(ErrorCode.SESSION_NOT_FOUND, {
    context: { sessionId },
    correlationId,
  });
}

export function agentNotConnectedError(sessionId: string, correlationId?: string): BashError {
  return new BashError(ErrorCode.AGENT_NOT_CONNECTED, {
    context: { sessionId },
    correlationId,
  });
}

export function agentBusyError(sessionId: string, correlationId?: string): BashError {
  return new BashError(ErrorCode.AGENT_BUSY, {
    context: { sessionId },
    correlationId,
  });
}

export function validationError(
  details: string,
  context?: BashErrorContext,
  correlationId?: string
): BashError {
  return new BashError(ErrorCode.VALIDATION_FAILED, {
    message: details,
    context,
    correlationId,
  });
}

export function socketNotConnectedError(): BashError {
  return new BashError(ErrorCode.SOCKET_NOT_CONNECTED);
}

export function envValidationError(details: string): BashError {
  return new BashError(ErrorCode.ENV_VALIDATION_FAILED, {
    message: details,
  });
}
